import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { Layout } from './components/Layout';
import {
  DashboardPage, DevicesPage, HistoryPage, ServiceHistoryPage, ServiceSettingsPage, ServiceMonitorPage,
  AlarmsPage, ControlPage, SettingsPage, ModbusPage, SchedulePage, LoginPage, TopologyPage,
  BorsihindPage, UsersPage, SystemPage, PlanComparePage, EconomicsHistoryPage, SolarHistoryPage, NotificationsPage, MarkedroidHistoryPage
} from './pages';
import { useWebSocket } from './hooks/useWebSocket';
import { ParksProvider } from './contexts/ParksContext';
import { AuthProvider, useAuth, setupAuthFetchInterceptor } from './contexts/AuthContext';
import { RoleGuard } from './components/RoleGuard';

/** Sisene marsruut, mis paigaldab globaalse 401 → kasutaja-tühjenduse. */
function FetchInterceptorBoot() {
  const { setUser } = useAuth();
  useEffect(() => {
    setupAuthFetchInterceptor(() => setUser(null));
  }, [setUser]);
  return null;
}

/**
 * Kaitstud sisutrakk — mountitakse ainult kui kasutaja on autenditud.
 * Sees on ParksProvider, WebSocket ja Layout.
 */
function ProtectedShell() {
  const { data: wsData, connected: wsConnected } = useWebSocket('/ws');
  return (
    <ParksProvider>
      <Routes>
        <Route
          path="/"
          element={
            <Layout
              connected={wsData?.connected}
              alarms={wsData?.alarms || []}
            />
          }
        >
          <Route index element={<DashboardPage wsData={wsData} wsConnected={wsConnected} />} />
          <Route path="borsihind" element={<BorsihindPage wsConnected={wsConnected} />} />
          <Route path="devices" element={<DevicesPage wsData={wsData} wsConnected={wsConnected} />} />
          <Route path="history" element={<HistoryPage wsConnected={wsConnected} />} />
          <Route path="economics-history" element={<EconomicsHistoryPage />} />
          <Route path="solar-history" element={<SolarHistoryPage />} />
          <Route path="markedroid-history" element={<MarkedroidHistoryPage />} />
          <Route path="notifications" element={<RoleGuard requiredRole="admin"><NotificationsPage /></RoleGuard>} />
          <Route path="service" element={<Navigate to="/service/history" replace />} />
          <Route path="service/history"  element={<ServiceHistoryPage  wsConnected={wsConnected} />} />
          <Route path="service/settings" element={<ServiceSettingsPage wsConnected={wsConnected} />} />
          <Route path="service/monitor"  element={<ServiceMonitorPage  wsConnected={wsConnected} />} />
          <Route path="alarms" element={<AlarmsPage wsData={wsData} wsConnected={wsConnected} />} />
          {/* Admin-ainult lehed — kuvavad "Funktsionaalsus ei ole kasutajale avatud" mitte-admin'idele */}
          <Route path="control"  element={<RoleGuard requiredRole="admin"><ControlPage  wsData={wsData} wsConnected={wsConnected} /></RoleGuard>} />
          <Route path="plan-compare" element={<RoleGuard requiredRole="admin"><PlanComparePage /></RoleGuard>} />
          <Route path="settings" element={<RoleGuard requireSuper><SettingsPage wsConnected={wsConnected} /></RoleGuard>} />
          <Route path="schedule" element={<RoleGuard requireSuper><SchedulePage wsData={wsData} wsConnected={wsConnected} /></RoleGuard>} />
          <Route path="modbus" element={<ModbusPage wsData={wsData} wsConnected={wsConnected} />} />
          <Route path="topology" element={<TopologyPage wsConnected={wsConnected} />} />
          <Route path="users" element={<RoleGuard requiredRole="admin"><UsersPage wsConnected={wsConnected} /></RoleGuard>} />
          <Route path="system" element={<RoleGuard requireSuper><SystemPage wsConnected={wsConnected} /></RoleGuard>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </ParksProvider>
  );
}

/** Tipp-tasandi marsruuter — eraldab sisselogimise ja kaitstud osa. */
function AppRouter() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: '#64748b' }}>
        Laadimine...
      </div>
    );
  }

  if (!user) {
    // Sisselogimata: ainult /login kättesaadav
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" state={{ from: location.pathname + location.search }} replace />} />
      </Routes>
    );
  }

  // Sisse logitud: /login marsruut suunab töölaule, ülejäänud kaitstud osa
  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/*" element={<ProtectedShell />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <FetchInterceptorBoot />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
